/**
 * Inbound-email turns (built by the infra `agent-inbound-email` Lambda) start
 * with a fixed ~600-char instruction preamble, then a `---` separator,
 * `Subject: <subject>`, and the body. That preamble is always identical and
 * unhelpful in the UI, so the console redacts it for display: the chat shows a
 * "via email" chip plus the subject/body, and the sessions list derives its
 * title from the subject (done server-side, where the untruncated text lives).
 *
 * Detection keys on the preamble's stable first sentence — kept in sync with
 * `EMAIL_MODE_PREAMBLE` in the infra inbound-email Lambda and the matching
 * `stripEmailPreamble` in the session-title derivation (infra + runtime). This
 * is a DISPLAY transform only: the stored turn text and what the agent saw are
 * unchanged, so agent interactions are unaffected.
 */

/** First sentence of the infra inbound-email `EMAIL_MODE_PREAMBLE`. */
const EMAIL_PREAMBLE_SENTINEL =
  'This request arrived by EMAIL and you are running non-interactively.';

export interface EmailRedaction {
  /** Whether the turn is an inbound-email turn (it starts with the preamble). */
  readonly isEmail: boolean;
  /** Text to display: the original for non-email turns, or the subject + body
   *  (preamble and its trailing `---` separator stripped) for email turns. */
  readonly body: string;
}

/**
 * Strip the inbound-email preamble for display, keeping the `Subject:` line and
 * body. Only the FIRST `---` separator (the one after the preamble) is removed,
 * so a later attachment-note `---` survives in the body.
 */
export function redactEmailPreamble(text: string): EmailRedaction {
  if (!text.trimStart().startsWith(EMAIL_PREAMBLE_SENTINEL)) {
    return { isEmail: false, body: text };
  }
  const sep = /\n[ \t]{0,20}---[ \t]{0,20}\n/.exec(text);
  const body = sep ? text.slice(sep.index + sep[0].length) : '';
  return { isEmail: true, body: body.trim() };
}
